import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { UserPlus, Save, KeyRound, UserX, UserCheck, Shield, Trash2 } from 'lucide-react';
import { formatDateTime } from '@/utils/dateFormat';

const ROLE_LABELS = { admin: 'Admin', redakteur: 'Redakteur' };
const ROLE_COLORS = { admin: 'bg-blue-100 text-blue-700', redakteur: 'bg-green-100 text-green-700' };

export default function AdminUsers() {
  const { admin: currentAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', first_name: '', last_name: '', role: 'redakteur' });
  const [resetPw, setResetPw] = useState('');
  const [editData, setEditData] = useState({ username: '', email: '', first_name: '', last_name: '', role: '' });

  const fetchUsers = () => api.get('/users').then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { fetchUsers(); }, []);

  const isAdmin = currentAdmin?.role === 'admin';

  const handleCreate = async () => {
    if (!newUser.username || !newUser.email || !newUser.password) { toast.error('Bitte alle Pflichtfelder ausfüllen'); return; }
    if (newUser.password.length < 8) { toast.error('Passwort muss mindestens 8 Zeichen haben'); return; }
    try {
      await api.post('/users', newUser);
      toast.success('Benutzer angelegt');
      setNewUser({ username: '', email: '', password: '', first_name: '', last_name: '', role: 'redakteur' });
      requestAnimationFrame(() => { setShowCreate(false); });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    if (user.id === currentAdmin?.id) { toast.error('Eigenen Account kann man nicht deaktivieren'); return; }
    if (!window.confirm(`Benutzer "${user.username}" wirklich ${newStatus === 'inactive' ? 'deaktivieren' : 'aktivieren'}?`)) return;
    try {
      await api.put(`/users/${user.id}`, { status: newStatus });
      toast.success(`Benutzer ${newStatus === 'active' ? 'aktiviert' : 'deaktiviert'}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleDelete = async (user) => {
    if (user.id === currentAdmin?.id) { toast.error('Eigenen Account kann man nicht löschen'); return; }
    if (!window.confirm(`Benutzer "${user.username}" wirklich endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      toast.success('Benutzer gelöscht');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleResetPassword = async () => {
    if (resetPw.length < 8) { toast.error('Passwort muss mindestens 8 Zeichen haben'); return; }
    try {
      await api.post(`/users/${showResetPw}/reset-password`, { new_password: resetPw });
      toast.success('Passwort zurückgesetzt');
      setShowResetPw(null);
      setResetPw('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleEdit = async () => {
    if (!editData.username) { toast.error('Benutzername darf nicht leer sein'); return; }
    try {
      await api.put(`/users/${showEdit}`, editData);
      toast.success('Benutzer aktualisiert');
      requestAnimationFrame(() => { setShowEdit(null); });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const openEdit = (user) => {
    setEditData({ username: user.username, email: user.email, first_name: user.first_name || '', last_name: user.last_name || '', role: user.role });
    setShowEdit(user.id);
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0E518D]">Benutzerverwaltung</h1>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid="create-user-btn">
            <UserPlus className="h-4 w-4 mr-1" /> Neuer Benutzer
          </Button>
        )}
      </div>

      <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Benutzer</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">E-Mail</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Rolle</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Letztes Login</th>
                  {isAdmin && <th className="text-right px-4 py-3 font-medium text-slate-600">Aktionen</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50/50" data-testid={`user-row-${user.username}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-800">{user.first_name || ''} {user.last_name || ''}</p>
                        <p className="text-xs text-slate-400">@{user.username}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[user.role] || 'bg-slate-100 text-slate-600'}`}>
                        <Shield className="h-3 w-3" />{ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={user.status === 'active' ? 'default' : 'secondary'} className={`text-xs ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                        {user.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{user.last_login ? formatDateTime(user.last_login) : '-'}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(user)} className="text-xs h-7 px-2" data-testid={`edit-user-${user.username}`}>
                            <Save className="h-3 w-3 mr-1" />Bearbeiten
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setShowResetPw(user.id); setResetPw(''); }} className="text-xs h-7 px-2" data-testid={`reset-pw-${user.username}`}>
                            <KeyRound className="h-3 w-3 mr-1" />Passwort
                          </Button>
                          {user.id !== currentAdmin?.id && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(user)} className={`text-xs h-7 px-2 ${user.status === 'active' ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}`} data-testid={`toggle-status-${user.username}`}>
                                {user.status === 'active' ? <><UserX className="h-3 w-3 mr-1" />Deaktivieren</> : <><UserCheck className="h-3 w-3 mr-1" />Aktivieren</>}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} className="text-xs h-7 px-2 text-red-500 hover:text-red-700" data-testid={`delete-user-${user.username}`}>
                                <Trash2 className="h-3 w-3 mr-1" />Löschen
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white" aria-describedby="create-user-desc">
          <DialogHeader><DialogTitle className="text-[#0E518D]">Neuen Benutzer anlegen</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4" id="create-user-desc">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Vorname</Label>
                <Input value={newUser.first_name} onChange={e => setNewUser(s => ({ ...s, first_name: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-user-firstname" />
              </div>
              <div>
                <Label className="text-sm">Nachname</Label>
                <Input value={newUser.last_name} onChange={e => setNewUser(s => ({ ...s, last_name: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-user-lastname" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Benutzername *</Label>
              <Input value={newUser.username} onChange={e => setNewUser(s => ({ ...s, username: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-user-username" />
            </div>
            <div>
              <Label className="text-sm">E-Mail *</Label>
              <Input type="email" value={newUser.email} onChange={e => setNewUser(s => ({ ...s, email: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-sm">Passwort * (mind. 8 Zeichen)</Label>
              <Input type="password" value={newUser.password} onChange={e => setNewUser(s => ({ ...s, password: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-user-password" />
            </div>
            <div>
              <Label className="text-sm">Rolle</Label>
              <select
                value={newUser.role}
                onChange={e => setNewUser(s => ({ ...s, role: e.target.value }))}
                className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
                data-testid="new-user-role"
              >
                <option value="admin">Admin</option>
                <option value="redakteur">Redakteur</option>
              </select>
            </div>
            <Button onClick={handleCreate} className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide" data-testid="submit-create-user">
              Benutzer anlegen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent className="bg-white" aria-describedby="edit-user-desc">
          <DialogHeader><DialogTitle className="text-[#0E518D]">Benutzer bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4" id="edit-user-desc">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Vorname</Label>
                <Input value={editData.first_name} onChange={e => setEditData(s => ({ ...s, first_name: e.target.value }))} className="mt-1 rounded-sm" data-testid="edit-user-firstname" />
              </div>
              <div>
                <Label className="text-sm">Nachname</Label>
                <Input value={editData.last_name} onChange={e => setEditData(s => ({ ...s, last_name: e.target.value }))} className="mt-1 rounded-sm" data-testid="edit-user-lastname" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Benutzername *</Label>
              <Input value={editData.username} onChange={e => setEditData(s => ({ ...s, username: e.target.value }))} className="mt-1 rounded-sm" data-testid="edit-user-username" />
            </div>
            <div>
              <Label className="text-sm">E-Mail</Label>
              <Input type="email" value={editData.email} onChange={e => setEditData(s => ({ ...s, email: e.target.value }))} className="mt-1 rounded-sm" data-testid="edit-user-email" />
            </div>
            <div>
              <Label className="text-sm">Rolle</Label>
              <select
                value={editData.role}
                onChange={e => setEditData(s => ({ ...s, role: e.target.value }))}
                className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
                data-testid="edit-user-role"
              >
                <option value="admin">Admin</option>
                <option value="redakteur">Redakteur</option>
              </select>
            </div>
            <Button onClick={handleEdit} className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide" data-testid="submit-edit-user">
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPw} onOpenChange={() => setShowResetPw(null)}>
        <DialogContent className="bg-white" aria-describedby="reset-pw-desc">
          <DialogHeader><DialogTitle className="text-[#0E518D]">Passwort zurücksetzen</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4" id="reset-pw-desc">
            <p className="text-sm text-slate-500">Der Benutzer muss das Passwort beim nächsten Login ändern.</p>
            <div>
              <Label className="text-sm">Neues Passwort (mind. 8 Zeichen)</Label>
              <Input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)} className="mt-1 rounded-sm" data-testid="reset-pw-input" />
            </div>
            <Button onClick={handleResetPassword} className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide" data-testid="submit-reset-pw">
              Passwort zurücksetzen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
